import React from 'react'
import { useTranslation } from 'react-i18next'
import { useProgressionStore } from './useProgressionStore.js'
import { useShopStore } from './useShopStore.js'
import { S } from '../locales/strings.js'

function getMsUntilUTCMidnight(): number {
  const now = new Date()
  const midnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1))
  return midnight.getTime() - now.getTime()
}

function formatCountdown(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export const DailyPackButton: React.FC = () => {
  const { t } = useTranslation()
  const { lastDailyPack, setLastDailyPack, packMeter, setPackMeter, ownedCardIds, addOwnedCards } =
    useProgressionStore()

  const todayUTC = new Date().toISOString().slice(0, 10)
  const claimed = lastDailyPack === todayUTC

  const [countdown, setCountdown] = React.useState(() => getMsUntilUTCMidnight())

  React.useEffect(() => {
    if (!claimed) return
    setCountdown(getMsUntilUTCMidnight())
    const id = setInterval(() => {
      setCountdown(getMsUntilUTCMidnight())
    }, 1000)
    return () => clearInterval(id)
  }, [claimed])

  function handleClaim(): void {
    if (claimed) return
    setLastDailyPack(todayUTC)
    setPackMeter(Math.min(100, packMeter + 20))

    const FALLBACK_POOL = ['archer', 'firemage', 'frost', 'lightning', 'shadow', 'goldmine', 'wall']
    const pool = ownedCardIds.length >= 3 ? ownedCardIds : FALLBACK_POOL
    const cards = [...pool].sort(() => Math.random() - 0.5).slice(0, 3)
    addOwnedCards(cards)

    useShopStore.getState().setPackResult(
      cards.map((cardId) => ({
        cardId,
        rarity: 'common' as const,
        isNew: !ownedCardIds.includes(cardId),
        category: 'tower' as const,
      }))
    )
  }

  if (!claimed) {
    return (
      <button
        onClick={handleClaim}
        style={{
          width: '100%',
          padding: '12px 0',
          fontSize: 16,
          fontWeight: 900,
          fontFamily: 'monospace',
          borderRadius: 8,
          border: '2px solid #C9A84C',
          background: 'linear-gradient(135deg, rgba(201,168,76,0.28), rgba(201,168,76,0.10))',
          color: '#C9A84C',
          cursor: 'pointer',
          minHeight: 44,
          boxSizing: 'border-box',
          boxShadow: '0 0 18px rgba(201,168,76,0.40), inset 0 0 10px rgba(201,168,76,0.08)',
          animation: 'ultimaze-glow-pulse 2s ease-in-out infinite',
          position: 'relative',
        }}
      >
        {t(S.DAILY_PACK_CLAIM)}
        <style>{`
          @keyframes ultimaze-glow-pulse {
            0%, 100% { box-shadow: 0 0 18px rgba(201,168,76,0.40), inset 0 0 10px rgba(201,168,76,0.08); }
            50% { box-shadow: 0 0 32px rgba(201,168,76,0.75), inset 0 0 18px rgba(201,168,76,0.18); }
          }
        `}</style>
      </button>
    )
  }

  return (
    <button
      disabled
      style={{
        width: '100%',
        padding: '12px 0',
        fontSize: 13,
        fontFamily: 'monospace',
        borderRadius: 8,
        border: '2px solid #3a3050',
        background: 'rgba(30,24,48,0.7)',
        color: '#555',
        cursor: 'default',
        minHeight: 44,
        boxSizing: 'border-box',
      }}
    >
      {t(S.DAILY_PACK_TOMORROW, { countdown: formatCountdown(countdown) })}
    </button>
  )
}
